import { Reveal, Eyebrow } from "@/lib/motion";
import { Check } from "lucide-react";
import { scrollToId } from "@/lib/smoothScroll";

const PLANS = [
  {
    name: "Starter",
    price: "49",
    fit: "Small ops teams getting started",
    features: ["AI Task Router", "Workflow Dashboards"],
    featured: false,
  },
  {
    name: "Growth",
    price: "99",
    fit: "Teams ready to enforce SOPs at scale",
    features: [
      "Everything in Starter",
      "SOP Automation",
      "AI Communication Agents",
    ],
    featured: true,
  },
  {
    name: "Enterprise",
    price: "199",
    fit: "Multi-site or compliance-heavy orgs",
    features: [
      "Everything in Growth",
      "SLA analytics",
      "Custom workflow builder",
      "Dedicated AI agent tuning",
      "SSO",
    ],
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="relative bg-bg-surface/45 py-24 backdrop-blur-sm lg:py-32">
      <div className="container-max band-pad">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow testId="pricing-eyebrow">Pricing</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-3xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Priced per user. Scoped by how far you enforce.
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative flex h-full flex-col rounded-2xl border p-8 transition-transform duration-300 hover:-translate-y-1 ${
                  p.featured
                    ? "border-signal/60 bg-bg-primary shadow-[0_0_0_1px_rgba(61,255,154,0.15),0_30px_80px_-40px_rgba(61,255,154,0.35)]"
                    : "border-hairline bg-bg-primary/40"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-signal px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-bg-primary">
                    Most teams
                  </span>
                )}
                <h3 className="font-display text-lg font-semibold text-ink">
                  {p.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-mono text-5xl font-medium tracking-tight text-ink">
                    ${p.price}
                  </span>
                  <span className="font-mono text-sm text-ink-muted">
                    /user/mo
                  </span>
                </div>
                <p className="mt-3 text-sm text-ink-muted">{p.fit}</p>

                <ul className="mt-7 flex flex-1 flex-col gap-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-ink">
                      <Check
                        size={16}
                        strokeWidth={2.5}
                        className="mt-0.5 shrink-0 text-signal"
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  onClick={() => scrollToId("contact")}
                  className={`mt-8 rounded-full px-5 py-3 font-sans text-sm font-semibold transition-transform duration-200 hover:scale-[1.02] active:scale-95 ${
                    p.featured
                      ? "bg-signal text-bg-primary"
                      : "border border-hairline bg-transparent text-ink hover:border-signal/50"
                  }`}
                >
                  Get a workflow audit
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 text-center font-mono text-xs text-ink-muted">
            Add-ons available on any plan: SLA analytics module · Custom workflow
            builder · Dedicated AI agent tuning.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
